import { type DragEndEvent } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import {
	IconDeviceDesktop,
	IconDeviceMobile,
	IconExternalLink,
	IconLoader2,
	IconPlus,
	IconSearch,
	IconSettings,
	IconSparkles,
	IconStack2,
} from "@tabler/icons-react"
import { LayoutGroup, motion } from "motion/react"
import { type FC, Fragment, type ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router"

import type { WebsiteEditPreview } from "@forento/core/models/aiWebsite"
import {
	type LandingPageBlock,
	type LandingPageExternalNavigationLink,
	type LandingPageSettings,
	type LandingPageSitePage,
	landingPageAnimateInStyleIds,
	landingPageAnimateInStyleLabels,
	landingPageButtonStyleIds,
	landingPageButtonStyles,
	landingPageFontIds,
	landingPageFonts,
	mergeLandingPageHeaderNavigation,
} from "@forento/core/models/landing"
import { blockDefinition, landingPageBlockTypes } from "@forento/core/models/landingBlock"
import { applyLandingPageStylePreset, landingPageStyles } from "@forento/core/models/landingStyle"
import { landingPagePageDescriptionMaxLength } from "@forento/core/schemas/landing"
import { toSlug } from "@forento/core/utilities/string"
import Tooltip from "@forento/ui/components/Tooltip"
import { getLandingThemeStyle, type LandingThemeSettings } from "@forento/ui/utilities/landingTheme"
import { cn, tw } from "@forento/ui/utilities/tailwind"

import AiGenerationFeedback from "~/components/AiGenerationFeedback"
import Button from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import EditableLinkField from "~/components/EditableLinkField"
import FullLoadingPage from "~/components/FullLoadingPage"
import { ArrowIcon } from "~/components/Icon"
import InputField, { InputTextArea } from "~/components/InputField"
import Link from "~/components/Link"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import SortableListContext from "~/contexts/SortableListContext"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { getInputErrors, query } from "~/utilities/trpc"

import AddBlockIndicator from "./AddBlockIndicator"
import AddBlockModal, { type AddBlockRequest } from "./AddBlockModal"
import AiCreatePageModal from "./AiCreatePageModal"
import AiPreviewBlocks, { getAiPreviewBlockDomId, getAiPreviewRemovedBlockDomId } from "./AiPreviewBlocks"
import AiReviewBar from "./AiReviewBar"
import AiWebsiteEditChat from "./AiWebsiteEditChat"
import BlockThumbnail from "./BlockThumbnail"
import BlockOutlineItem from "./blocks/BlockOutlineItem"
import BlockPreview from "./blocks/preview/BlockPreview"
import LandingPagePreviewFrame from "./LandingPagePreviewFrame"
import SidebarSection, { useSidebarSectionCollapse } from "./SidebarSection"
import useWebsiteAiEdit from "./useWebsiteAiEdit"
import WebsiteStylePicker from "./WebsiteStylePicker"

const previewFrameId = "landing-page-editor-preview-frame"
const previewScrollContainerId = "landing-page-editor-preview-scroll-container"
const aiPanelCollapsedKey = "website-ai-panel-collapsed"
const landingPagePathMaxLength = 120
const searchablePageCount = 8
const previewDeviceConfig = {
	desktop: {
		label: "Desktop",
		detail: "1440 px",
		icon: IconDeviceDesktop,
		frameClassName: tw`w-full max-w-360 shrink-0 self-start border-0 bg-white shadow-xl ring-1 ring-black/5`,
	},
	mobile: {
		label: "Mobile",
		detail: "390 px",
		icon: IconDeviceMobile,
		frameClassName: tw`w-102.5 max-w-full shrink-0 self-start rounded-4xl border-10 border-gray-900 bg-white shadow-2xl ring-1 ring-black/10`,
	},
} satisfies Record<
	string,
	{
		label: string
		detail: string
		icon: FC<{ className?: string }>
		frameClassName: string
	}
>

type PreviewDevice = keyof typeof previewDeviceConfig

type Props = { generationId?: number }
const LandingPageEditor: FC<Props> = ({ generationId }) => {
	const [pageGenerationIds, setPageGenerationIds] = useState<Record<number, number>>({})
	const platform = usePlatform()!
	const user = useUser()
	const [searchParams, setSearchParams] = useSearchParams()
	const alert = useAlert()
	const toast = useToast()

	const {
		data: pages = [],
		isLoading: pagesLoading,
		error: pagesError,
		refetch: refetchPages,
	} = query.landing.pages.list.useQuery(undefined, {
		refetchOnWindowFocus: false,
	})
	const {
		data: externalNavigationLinks = [],
		error: externalNavigationLinksError,
		refetch: refetchExternalNavigationLinks,
	} = query.landing.externalNavigationLinks.list.useQuery(undefined, { refetchOnWindowFocus: false })

	const [selectedPageId, setSelectedPageId] = useState<number | null>(null)
	const [pageSearch, setPageSearch] = useState("")
	const [isCreatePageOpen, setCreatePageOpen] = useState(false)
	const [isManagePagesOpen, setManagePagesOpen] = useState(false)

	// A callback ref rather than an effect on the selection, since the selected row can mount later
	// than the selection changes: the editor is still behind its loading gate when the first page is
	// selected, and clearing a search re-mounts a selected row it had filtered out. It is detached
	// while the section is collapsed so scrollIntoView cannot scroll the height-0 collapse container,
	// and re-attaching on expand is what scrolls the selected row into view then.
	const handleSelectedPageRef = useCallback((element: HTMLButtonElement | null) => {
		element?.scrollIntoView({ block: "nearest" })
	}, [])

	const {
		data: selectedPageData,
		error: selectedPageError,
		refetch: refetchSelectedPage,
	} = query.landing.pages.get.useQuery(
		{ pageId: selectedPageId ?? 0 },
		{ enabled: selectedPageId !== null, refetchOnWindowFocus: false },
	)

	const [blocks, setBlocks] = useState<LandingPageBlock[]>([])
	const nextBlockId = useRef(0)
	const [blockIds, setBlockIds] = useState<number[]>([])
	const movedBlockId = useRef<number | null>(null)
	const [scrollTargetDomId, setScrollTargetDomId] = useState<string | null>(null)
	const [editingBlockIndex, setEditingBlockIndex] = useState<number | null>(null)
	const [previewDevice, setPreviewDevice] = useState<PreviewDevice>("desktop")
	const [isAiCreatePageModalOpen, setAiCreatePageModalOpen] = useState(searchParams.get("ai") === "true")
	useEffect(() => {
		if (!searchParams.has("ai")) return
		setSearchParams(
			params => {
				params.delete("ai")
				return params
			},
			{ replace: true },
		)
	}, [searchParams, setSearchParams])
	const [isAiPanelExpanded, setAiPanelExpanded] = useState(() => localStorage.getItem(aiPanelCollapsedKey) !== "true")
	const [addBlockRequest, setAddBlockRequest] = useState<AddBlockRequest | null>(null)

	const [isPagesSectionCollapsed, togglePagesSection] = useSidebarSectionCollapse("pages")
	const [isStyleSectionCollapsed, toggleStyleSection] = useSidebarSectionCollapse("style")
	const [isPasswordSectionCollapsed, togglePasswordSection] = useSidebarSectionCollapse("password")
	const [isAddBlockSectionCollapsed, toggleAddBlockSection] = useSidebarSectionCollapse("add-block")
	const [isBlocksSectionCollapsed, toggleBlocksSection] = useSidebarSectionCollapse("blocks")

	const {
		data: landingSettings,
		error: landingSettingsError,
		refetch: refetchLandingSettings,
	} = query.landing.settings.get.useQuery(undefined, {
		refetchOnWindowFocus: false,
	})

	const updateLandingSettingsMutation = query.landing.settings.update.useMutation()
	const updatePageBlocksMutation = query.landing.pages.updateBlocks.useMutation()
	const [sitePassword, setSitePassword] = useState("")

	const handleSaveSitePassword = async () => {
		const password = sitePassword.trim()
		if (password.length === 0 || updateLandingSettingsMutation.isPending) return

		try {
			await updateLandingSettingsMutation.mutateAsync({ password })
			await refetchLandingSettings()
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to save the website password.")
			return
		}
		setSitePassword("")
		toast.showChangesSaved("Password protection")
	}

	const handleDisableSitePassword = async () => {
		if (updateLandingSettingsMutation.isPending) return

		try {
			await updateLandingSettingsMutation.mutateAsync({ password: null })
			await refetchLandingSettings()
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to turn off password protection.")
			return
		}
		toast.showChangesSaved("Password protection")
	}

	const canUseAi = platform.enabledFeatures.includes("ai-generated-courses")
	// Forento AI reads and rewrites the saved site, so unsaved block edits would be silently replaced
	// by whatever it proposes.
	const hasUnsavedBlockChanges =
		selectedPageData !== undefined && JSON.stringify(blocks) !== JSON.stringify(selectedPageData.blocks)

	const ai = useWebsiteAiEdit({
		isEnabled: canUseAi,
		hasUnsavedChanges: hasUnsavedBlockChanges,
		onApplied: async () => {
			setEditingBlockIndex(null)
			await Promise.all([refetchPages(), refetchSelectedPage(), refetchLandingSettings(), user.reload()])
			toast.showSuccess("Changes applied to your website")
		},
	})
	const { setPreviewVisible: setAiPreviewVisible, refetchPreview: refetchAiPreview } = ai

	useEffect(() => {
		if (pages.length === 0) {
			setSelectedPageId(null)
			return
		}
		if (selectedPageId === null || !pages.some(p => p.id === selectedPageId)) {
			setSelectedPageId(pages[0]!.id)
		}
	}, [pages, selectedPageId])

	useEffect(() => {
		if (selectedPageId !== null && selectedPageData?.blocks) {
			setAddBlockRequest(null)
			setBlocks(selectedPageData.blocks)
			const ids = selectedPageData.blocks.map(() => nextBlockId.current++)
			setBlockIds(ids)
		}
		if (selectedPageId === null) {
			setBlocks([])
			setBlockIds([])
		}
	}, [selectedPageData, selectedPageId])

	const handleSave = useCallback(async () => {
		if (selectedPageId === null || updatePageBlocksMutation.isPending) return

		try {
			await updatePageBlocksMutation.mutateAsync({ pageId: selectedPageId, blocks })
			toast.showChangesSaved("Landing page")
			await Promise.all([refetchSelectedPage(), refetchAiPreview()])
		} catch (error) {
			console.error(error)
			await alert.show("Error", getLandingPageSaveErrorMessage(error))
		}
	}, [blocks, selectedPageId, updatePageBlocksMutation, toast, alert, refetchSelectedPage, refetchAiPreview])

	const handleAddBlock = (type: LandingPageBlock["type"], insertionIndex: number) => {
		const newBlock = blockDefinition(type).createDefaultBlock()
		const safeInsertionIndex = Math.max(0, Math.min(insertionIndex, blocks.length))
		const newBlocks = [...blocks.slice(0, safeInsertionIndex), newBlock, ...blocks.slice(safeInsertionIndex)]
		const newBlockId = nextBlockId.current++
		setBlocks(newBlocks)
		setBlockIds(prev => [...prev.slice(0, safeInsertionIndex), newBlockId, ...prev.slice(safeInsertionIndex)])
		// Editing the page is only visible on the page as it stands, so it drops the AI preview.
		setAiPreviewVisible(false)
		setScrollTargetDomId(getBlockDomId(newBlockId))
		setEditingBlockIndex(safeInsertionIndex)
		setAddBlockRequest(null)
		toast.showSuccess(
			`${blockDefinition(type).label} block added${getInsertionToastSuffix(blocks, safeInsertionIndex)}`,
		)
	}

	useEffect(() => {
		if (scrollTargetDomId === null) return

		let animationFrame: number | null = null
		let attempts = 0
		const scrollToTarget = () => {
			attempts += 1
			scrollElementIntoPreview(scrollTargetDomId)
			if (attempts < 45) {
				animationFrame = window.requestAnimationFrame(scrollToTarget)
			}
		}
		animationFrame = window.requestAnimationFrame(scrollToTarget)
		const timeout = window.setTimeout(() => {
			setScrollTargetDomId(current => (current === scrollTargetDomId ? null : current))
		}, 2600)

		return () => {
			if (animationFrame !== null) window.cancelAnimationFrame(animationFrame)
			window.clearTimeout(timeout)
		}
	}, [scrollTargetDomId])

	const handleDeleteBlock = async (index: number) => {
		const dialog = await alert.confirm("Delete block", "Are you sure you want to delete this block?")
		if (!dialog.result) return

		setBlocks(prev => prev.filter((_, i) => i !== index))
		setBlockIds(prev => prev.filter((_, i) => i !== index))
		setEditingBlockIndex(null)
		dialog.close()
	}

	const handleBlockChange = useCallback((index: number, block: LandingPageBlock) => {
		setBlocks(prev => {
			const newBlocks = [...prev]
			newBlocks[index] = block
			return newBlocks
		})
	}, [])

	const handleReorderBlock = (fromIndex: number, toIndex: number) => {
		const movedId = blockIds[fromIndex]
		if (movedId === undefined || fromIndex === toIndex) return

		movedBlockId.current = movedId
		setEditingBlockIndex(prev => {
			if (prev === null) return null
			if (prev === fromIndex) return toIndex
			if (fromIndex < toIndex && prev > fromIndex && prev <= toIndex) return prev - 1
			if (fromIndex > toIndex && prev >= toIndex && prev < fromIndex) return prev + 1
			return prev
		})
		setBlocks(prev => arrayMove(prev, fromIndex, toIndex))
		setBlockIds(prev => arrayMove(prev, fromIndex, toIndex))
	}

	const handleBlockDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event
			if (!over || active.id === over.id) return

			const fromIndex = blockIds.indexOf(Number(active.id))
			const toIndex = blockIds.indexOf(Number(over.id))
			if (fromIndex === -1 || toIndex === -1) return

			handleReorderBlock(fromIndex, toIndex)
		},
		[blockIds, handleReorderBlock],
	)

	const handlePreviewFrameHeightChange = useCallback(() => {
		if (scrollTargetDomId === null) return
		window.requestAnimationFrame(() => {
			scrollElementIntoPreview(scrollTargetDomId)
		})
	}, [scrollTargetDomId])

	const handleAiPanelExpandedChange = useCallback((isExpanded: boolean) => {
		setAiPanelExpanded(isExpanded)
		localStorage.setItem(aiPanelCollapsedKey, (!isExpanded).toString())
	}, [])

	// A proposal is resolved against the saved site, so the editor is locked until it is applied or
	// discarded rather than letting an edit land underneath it and silently skip half of it.
	const isLockedByAiProposal = ai.undecidedTurn !== null
	const aiPreview = ai.isPreviewVisible ? ai.preview : null
	const pageGenerationId = selectedPageId === null ? undefined : pageGenerationIds[selectedPageId]
	const feedbackGeneration =
		pageGenerationId !== undefined
			? { id: pageGenerationId, type: "landing-page-additional-page" as const }
			: generationId !== undefined
				? { id: generationId, type: "landing-page" as const }
				: null

	const aiPreviewPage = aiPreview?.pages.find(page => page.pageId === selectedPageId) ?? null
	const firstAiChangeDomId = getFirstAiChangeDomId(aiPreviewPage)

	// The point of the preview is what the proposal does to the page, so it opens on the first change
	// rather than at the top of a page whose changes are further down.
	useEffect(() => {
		if (firstAiChangeDomId === null) return
		setScrollTargetDomId(firstAiChangeDomId)
	}, [firstAiChangeDomId])

	if (pagesError || selectedPageError || landingSettingsError || externalNavigationLinksError) {
		return <p>Failed to load landing page.</p>
	}
	if (pagesLoading || selectedPageId === null || !selectedPageData || !landingSettings) {
		return <FullLoadingPage text="Loading your website..." />
	}

	const previewUrl = `//${platform.domain.landingPage ?? platform.domain.platform}`
	const saveIsPending = updatePageBlocksMutation.isPending

	const isPageListSearchable = pages.length > searchablePageCount
	const trimmedPageSearch = pageSearch.trim().toLowerCase()
	const isSearchingPages = isPageListSearchable && trimmedPageSearch.length > 0
	const visiblePages = pages.filter(
		page =>
			!isSearchingPages ||
			(page.title ?? "").toLowerCase().includes(trimmedPageSearch) ||
			page.path.toLowerCase().includes(trimmedPageSearch),
	)

	const selectedPageLabel = pages.find(p => p.id === selectedPageId)?.title ?? "Untitled"
	const selectedPagePath = pages.find(p => p.id === selectedPageId)?.path ?? "/"
	const headerNavigationPagesForPreview = mergeLandingPageHeaderNavigation(
		pages.filter(page => !page.isHiddenInHeaderNavigation),
		externalNavigationLinks,
	)
	const footerNavigationPagesForPreview = pages
		.filter(p => !p.isHiddenInFooterNavigation)
		.map(p => ({
			id: p.id,
			path: p.path,
			title: p.title,
			navigationGroup: p.navigationGroup,
		}))
	const navigationPreview = {
		headerPages: headerNavigationPagesForPreview,
		footerPages: footerNavigationPagesForPreview,
		currentPath: selectedPagePath,
	}
	const landingStyle = landingPageStyles[landingSettings.styleId]
	const landingFont = landingPageFonts[landingSettings.fontId]
	const landingButtonStyle = landingPageButtonStyles[landingSettings.buttonStyleId]
	const previewDeviceSettings = previewDeviceConfig[previewDevice]
	const currentThemeStyle = getLandingThemeStyle(platform.primaryColor, landingSettings)
	const previewStyle =
		aiPreview?.style != null
			? getLandingThemeStyle(
					aiPreview.style.primaryColor !== null
						? { value: aiPreview.style.primaryColor, isCustom: true }
						: platform.primaryColor,
					getAiPreviewThemeSettings(landingSettings, aiPreview.style),
				)
			: currentThemeStyle
	const aiPreviewBlocks = aiPreviewPage?.blocks ?? blocks.map(block => ({ block, change: null, previousBlock: null }))
	const changedAiPageIds = new Set(ai.preview?.pages.map(page => page.pageId) ?? [])

	return (
		<>
			<div className="flex items-center justify-between border-b border-black/6 bg-white px-6 py-4 shadow-sm backdrop-blur">
				<div className="flex items-center gap-3">
					<Button
						variant="secondary"
						onClick={routes.main.index()}
						className="h-10 rounded-full px-3 py-2 text-sm font-medium"
					>
						<ArrowIcon direction="left" className="size-4" />
						<span>Back</span>
					</Button>
					<h1 className="m-0 text-xl font-bold text-black">Website editor</h1>
				</div>
				<div className="flex items-center gap-2.5">
					{canUseAi && (
						<Button
							variant="secondary"
							onClick={() => handleAiPanelExpandedChange(!isAiPanelExpanded)}
							className={cn(isAiPanelExpanded && "bg-primary/10")}
						>
							<IconSparkles className="size-4" />
							{isAiPanelExpanded ? "Hide Forento AI" : "Edit with Forento AI"}
						</Button>
					)}
					<Button variant="secondary" onClick={previewUrl} newTab>
						View live site
					</Button>
					<Button variant="primary" onClick={handleSave} isLoading={saveIsPending} isDisabled={saveIsPending}>
						Save changes
					</Button>
				</div>
			</div>
			<AiReviewBar ai={ai} selectedPageId={selectedPageId} />

			<div className="flex flex-1 overflow-hidden bg-gray-50">
				<div className="flex w-96 flex-col gap-8 overflow-y-auto border-r border-black/6 bg-white p-6 shadow-md">
					{feedbackGeneration && <AiGenerationFeedback {...feedbackGeneration} />}
					{isLockedByAiProposal && (
						<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
							<p className="text-sm text-amber-900">
								Editing is paused while Forento AI has proposed changes. Apply or discard them to
								continue editing your website yourself.
							</p>
						</div>
					)}
					<SidebarSection
						title="Pages"
						detail={
							<>
								{pages.length} {pages.length === 1 ? "page" : "pages"}
								{isSearchingPages && ` · ${visiblePages.length} matching`}
							</>
						}
						isCollapsed={isPagesSectionCollapsed}
						onToggle={togglePagesSection}
					>
						{isPageListSearchable && (
							<label className="relative block">
								<span className="sr-only">Search pages</span>
								<IconSearch className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-gray-400" />
								<input
									type="search"
									value={pageSearch}
									onChange={event => setPageSearch(event.target.value)}
									placeholder="Search pages"
									className="focus:border-primary focus:ring-primary/15 h-9 w-full rounded-lg border border-black/10 bg-white pr-3 pl-8 text-sm text-gray-900 transition-shadow outline-none placeholder:text-gray-400 focus:ring-2"
								/>
							</label>
						)}
						<div
							className={cn(
								"flex flex-col gap-1.5",
								isPageListSearchable && "-m-0.5 max-h-96 overflow-y-auto p-0.5",
							)}
						>
							{isSearchingPages && visiblePages.length === 0 && (
								<div className="rounded-lg border border-dashed border-black/10 px-3 py-4 text-center text-sm text-gray-500">
									No pages match your search.
								</div>
							)}
							{visiblePages.map(page => {
								const isSelected = page.id === selectedPageId
								return (
									<button
										key={page.id}
										ref={isSelected && !isPagesSectionCollapsed ? handleSelectedPageRef : undefined}
										type="button"
										onClick={() => setSelectedPageId(page.id)}
										className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-all ${
											isSelected
												? "border-indigo-300 bg-indigo-50 shadow-sm ring-1 ring-indigo-100"
												: "border-black/10 bg-white hover:border-gray-300 hover:bg-gray-50"
										}`}
									>
										<div className="min-w-0">
											<div className="flex items-center gap-1.5">
												<span className="truncate text-sm font-semibold text-gray-900">
													{page.title ?? page.path}
												</span>
												{changedAiPageIds.has(page.id) && (
													<Tooltip tooltip="Forento AI proposed changes on this page">
														<IconSparkles className="text-primary size-3.5 shrink-0" />
													</Tooltip>
												)}
											</div>
											<div className="truncate text-xs text-gray-500">{page.path}</div>
										</div>
										<span
											className={cn(
												"shrink-0 rounded-full px-2 py-1 text-[10px] font-bold tracking-wide uppercase",
												getNavigationVisibilityBadgeClassName(page),
											)}
										>
											{getNavigationVisibilityLabel(page)}
										</span>
									</button>
								)
							})}
						</div>
						<div className="grid grid-cols-2 gap-2">
							<Button
								variant="primary"
								onClick={() => setCreatePageOpen(true)}
								isDisabled={isLockedByAiProposal}
								className="h-10 rounded-lg px-3 py-2 text-sm"
							>
								<IconPlus className="size-4" />
								Create page
							</Button>
							<Button
								variant="secondary"
								onClick={() => setManagePagesOpen(true)}
								isDisabled={isLockedByAiProposal}
								className="h-10 rounded-lg px-3 py-2 text-sm"
							>
								<IconSettings className="size-4" />
								Manage pages
							</Button>
							{canUseAi && (
								<Button
									variant="secondary"
									onClick={() => setAiCreatePageModalOpen(true)}
									isDisabled={isLockedByAiProposal}
									className="col-span-2 h-10 rounded-lg px-3 py-2 text-sm"
								>
									<IconSparkles className="size-4" />
									Generate page with AI
								</Button>
							)}
						</div>
					</SidebarSection>
					<SidebarSection
						title="Website style"
						isCollapsed={isStyleSectionCollapsed}
						onToggle={toggleStyleSection}
					>
						<div className="flex flex-col gap-2 rounded-lg border border-black/10 bg-white p-3 shadow-sm">
							<span className="text-sm font-semibold text-gray-700">Style</span>
							<WebsiteStylePicker
								value={landingSettings.styleId}
								brandColor={platform.primaryColor}
								usesBrandColor={landingSettings.usesBrandColor}
								isDisabled={updateLandingSettingsMutation.isPending || isLockedByAiProposal}
								onChange={async styleId => {
									if (styleId === landingSettings.styleId) return
									try {
										await updateLandingSettingsMutation.mutateAsync({ styleId })
										await refetchLandingSettings()
									} catch (error) {
										console.error(error)
										await alert.show("Error", "Failed to update the website style.")
										return
									}
									toast.showChangesSaved("Website style")
								}}
							/>
							<span className="text-xs leading-relaxed text-gray-500">
								{landingStyle.description} Picking a style also sets its font and buttons, which you can
								still change below.
							</span>
						</div>
						<div className="flex flex-col gap-2 rounded-lg border border-black/10 bg-white p-3 shadow-sm">
							<div className="flex items-center justify-between gap-2">
								<span className="text-sm font-semibold text-gray-700">Brand color</span>
								{platform.primaryColor.isCustom && (
									<span
										className="size-5 rounded-full border border-black/10"
										style={{ backgroundColor: platform.primaryColor.value }}
									/>
								)}
							</div>
							{platform.primaryColor.isCustom ? (
								<>
									<Checkbox
										label="Use my academy brand color"
										isChecked={landingSettings.usesBrandColor}
										isDisabled={updateLandingSettingsMutation.isPending || isLockedByAiProposal}
										onChange={async usesBrandColor => {
											try {
												await updateLandingSettingsMutation.mutateAsync({ usesBrandColor })
												await refetchLandingSettings()
											} catch (error) {
												console.error(error)
												await alert.show("Error", "Failed to update the brand color setting.")
												return
											}
											toast.showChangesSaved("Brand color")
										}}
									/>
									<span className="text-xs leading-relaxed text-gray-500">
										Buttons and highlights use your academy brand color instead of the style's own
										accent.
									</span>
								</>
							) : (
								<span className="text-xs leading-relaxed text-gray-500">
									Buttons and highlights use the style's accent. Set a brand color under{" "}
									<Link
										to={routes.settings.customizeAcademy()}
										className="text-primary font-semibold hover:underline"
									>
										Customize academy
									</Link>{" "}
									to use it on the website instead.
								</span>
							)}
						</div>
						<label className="flex flex-col gap-2 rounded-lg border border-black/10 bg-white p-3 shadow-sm">
							<span className="text-sm font-semibold text-gray-700">Font</span>
							<select
								aria-label="Website font"
								className="h-10 cursor-pointer rounded-md border border-black/10 bg-white px-3 text-sm font-semibold text-gray-900 outline-none hover:bg-gray-50 disabled:cursor-auto disabled:bg-gray-50 disabled:text-gray-400"
								value={landingSettings.fontId}
								disabled={updateLandingSettingsMutation.isPending || isLockedByAiProposal}
								onChange={async e => {
									const fontId = landingPageFontIds.find(id => id === e.target.value)
									if (!fontId) return
									await updateLandingSettingsMutation.mutateAsync({ fontId })
									await refetchLandingSettings()
									toast.showChangesSaved("Website font")
								}}
							>
								{landingPageFontIds.map(fontId => (
									<option key={fontId} value={fontId}>
										{landingPageFonts[fontId].label}
									</option>
								))}
							</select>
							<span className="text-xs leading-relaxed text-gray-500">{landingFont.description}</span>
						</label>
						<label className="flex flex-col gap-2 rounded-lg border border-black/10 bg-white p-3 shadow-sm">
							<span className="text-sm font-semibold text-gray-700">Buttons</span>
							<select
								aria-label="Website button style"
								className="h-10 cursor-pointer rounded-md border border-black/10 bg-white px-3 text-sm font-semibold text-gray-900 outline-none hover:bg-gray-50 disabled:cursor-auto disabled:bg-gray-50 disabled:text-gray-400"
								value={landingSettings.buttonStyleId}
								disabled={updateLandingSettingsMutation.isPending || isLockedByAiProposal}
								onChange={async e => {
									const buttonStyleId = landingPageButtonStyleIds.find(id => id === e.target.value)
									if (!buttonStyleId) return
									try {
										await updateLandingSettingsMutation.mutateAsync({ buttonStyleId })
										await refetchLandingSettings()
									} catch (error) {
										console.error(error)
										await alert.show("Error", "Failed to update the button style.")
										return
									}
									toast.showChangesSaved("Button style")
								}}
							>
								{landingPageButtonStyleIds.map(buttonStyleId => (
									<option key={buttonStyleId} value={buttonStyleId}>
										{landingPageButtonStyles[buttonStyleId].label}
									</option>
								))}
							</select>
							<div
								className="landing-page flex justify-center rounded-md px-3 py-4"
								style={currentThemeStyle}
							>
								<span className="landing-button px-5 py-2.5 text-sm font-semibold">Button</span>
							</div>
							<span className="text-xs leading-relaxed text-gray-500">
								{landingButtonStyle.description}
							</span>
						</label>
						<label className="flex flex-col gap-2 rounded-lg border border-black/10 bg-white p-3 shadow-sm">
							<span className="text-sm font-semibold text-gray-700">Animate in</span>
							<select
								aria-label="Website animate-in style"
								className="h-10 cursor-pointer rounded-md border border-black/10 bg-white px-3 text-sm font-semibold text-gray-900 outline-none hover:bg-gray-50 disabled:cursor-auto disabled:bg-gray-50 disabled:text-gray-400"
								value={landingSettings.animateInStyle}
								disabled={updateLandingSettingsMutation.isPending || isLockedByAiProposal}
								onChange={async e => {
									const animateInStyle = landingPageAnimateInStyleIds.find(
										id => id === e.target.value,
									)
									if (!animateInStyle) return
									await updateLandingSettingsMutation.mutateAsync({ animateInStyle })
									await refetchLandingSettings()
									toast.showChangesSaved("Animation style")
								}}
							>
								{landingPageAnimateInStyleIds.map(animateInStyle => (
									<option key={animateInStyle} value={animateInStyle}>
										{landingPageAnimateInStyleLabels[animateInStyle]}
									</option>
								))}
							</select>
						</label>
					</SidebarSection>
					<SidebarSection
						title="Password protection"
						isCollapsed={isPasswordSectionCollapsed}
						onToggle={togglePasswordSection}
					>
						<div className="flex flex-col gap-2 rounded-lg border border-black/10 bg-white p-3 shadow-sm">
							<div className="flex items-center justify-between gap-2">
								<span className="text-sm font-semibold text-gray-700">Password</span>
								<span
									className={cn(
										"rounded-full px-2 py-1 text-[10px] font-bold tracking-wide uppercase",
										landingSettings.isPasswordProtected
											? "bg-emerald-100 text-emerald-800"
											: "bg-gray-100 text-gray-500",
									)}
								>
									{landingSettings.isPasswordProtected ? "On" : "Off"}
								</span>
							</div>
							<input
								aria-label="Website password"
								type="password"
								autoComplete="new-password"
								data-sentry-mask
								value={sitePassword}
								placeholder={landingSettings.isPasswordProtected ? "New password" : "Password"}
								maxLength={200}
								disabled={updateLandingSettingsMutation.isPending || isLockedByAiProposal}
								onChange={e => setSitePassword(e.target.value)}
								onKeyDown={e => {
									if (e.key === "Enter") void handleSaveSitePassword()
								}}
								className="h-10 rounded-md border border-black/10 bg-white px-3 text-sm font-semibold text-gray-900 outline-none placeholder:font-normal placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-400"
							/>
							<Button
								variant="primary"
								onClick={handleSaveSitePassword}
								isDisabled={
									sitePassword.trim().length === 0 ||
									updateLandingSettingsMutation.isPending ||
									isLockedByAiProposal
								}
								className="h-10 rounded-lg px-3 py-2 text-sm"
							>
								{landingSettings.isPasswordProtected ? "Change password" : "Turn on protection"}
							</Button>
							{landingSettings.isPasswordProtected && (
								<Button
									variant="secondary"
									onClick={handleDisableSitePassword}
									isDisabled={updateLandingSettingsMutation.isPending || isLockedByAiProposal}
									className="h-10 rounded-lg px-3 py-2 text-sm"
								>
									Turn off protection
								</Button>
							)}
							<span className="text-xs leading-relaxed text-gray-500">
								Visitors must enter this password to view your website — handy until you launch. It does
								not protect your academy.
							</span>
						</div>
					</SidebarSection>
					<SidebarSection
						title="Add block"
						subtitle="Choose a block, then place it on the page."
						isCollapsed={isAddBlockSectionCollapsed}
						onToggle={toggleAddBlockSection}
					>
						<div className="grid grid-cols-2 gap-2">
							{landingPageBlockTypes.map(type => (
								<button
									key={type}
									type="button"
									disabled={isLockedByAiProposal}
									onClick={() => setAddBlockRequest({ mode: "choose-position", type })}
									className={cn(
										"relative flex flex-col gap-2 overflow-hidden rounded-lg border-2 border-gray-200 bg-white px-3 py-3 text-left text-sm font-semibold text-gray-700 transition-all duration-200 ease-in-out",
										isLockedByAiProposal
											? "cursor-auto opacity-50"
											: "cursor-pointer before:absolute before:top-0 before:-left-full before:h-full before:w-full before:bg-linear-to-r before:from-transparent before:via-white/40 before:to-transparent before:transition-[left] before:duration-500 before:content-[''] hover:-translate-y-0.5 hover:border-transparent hover:bg-linear-to-br hover:from-indigo-400 hover:to-purple-600 hover:text-white hover:shadow-lg hover:before:left-full active:translate-y-0",
									)}
								>
									<BlockThumbnail type={type} className="relative w-full rounded" />
									{blockDefinition(type).label}
								</button>
							))}
						</div>
					</SidebarSection>
					<SidebarSection
						title={`Blocks (${blocks.length}) — ${selectedPageLabel}`}
						isCollapsed={isBlocksSectionCollapsed}
						onToggle={toggleBlocksSection}
					>
						<SortableListContext items={blockIds} onDragEnd={handleBlockDragEnd}>
							<div className="flex flex-col gap-1.5">
								{blocks.map((block, index) => (
									<BlockOutlineItem
										key={blockIds[index]}
										block={block}
										blockId={blockIds[index]!}
										index={index}
										isReorderDisabled={isLockedByAiProposal}
										onSelect={() => {
											// A locked editor renders the read-only preview's own sections, so an
											// outline click lands on the section at the same position in it.
											scrollElementIntoPreview(
												isLockedByAiProposal
													? getAiPreviewBlockDomId(index)
													: getBlockDomId(blockIds[index]!),
											)
										}}
									/>
								))}
							</div>
						</SortableListContext>
					</SidebarSection>
				</div>

				<div className="relative flex flex-1 flex-col overflow-hidden bg-linear-to-br from-gray-100 to-slate-300 before:pointer-events-none before:absolute before:top-0 before:right-0 before:left-0 before:h-52 before:bg-linear-to-b before:from-white/10 before:to-transparent before:content-['']">
					<div className="relative flex items-center justify-between gap-4 border-b border-black/6 bg-white/95 px-6 py-3 shadow-sm backdrop-blur">
						<div className="min-w-0">
							<div className="text-xs font-bold tracking-wide text-gray-500 uppercase">Preview</div>
							<div className="truncate text-sm text-gray-500">
								{selectedPageLabel} / {previewDeviceSettings.label} / {previewDeviceSettings.detail}
							</div>
						</div>
						<div className="flex rounded-lg border border-black/10 bg-gray-100 p-1">
							{Object.entries(previewDeviceConfig).map(([device, config]) => {
								const Icon = config.icon
								const isSelected = device === previewDevice
								return (
									<button
										key={device}
										type="button"
										aria-pressed={isSelected}
										onClick={() => setPreviewDevice(device as PreviewDevice)}
										className={cn(
											"flex h-9 cursor-pointer items-center gap-2 rounded-md px-3 text-sm font-semibold transition-colors",
											isSelected
												? "bg-white text-gray-950 shadow-sm"
												: "text-gray-500 hover:bg-white/70 hover:text-gray-900",
										)}
									>
										<Icon className="size-4" />
										<span>{config.label}</span>
									</button>
								)
							})}
						</div>
					</div>
					<div
						id={previewScrollContainerId}
						className="relative flex-1 overflow-x-hidden overflow-y-auto p-6 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-black/20 hover:[&::-webkit-scrollbar-thumb]:bg-black/30 [&::-webkit-scrollbar-track]:bg-white/10"
					>
						<div className="flex min-h-full justify-center">
							<LandingPagePreviewFrame
								id={previewFrameId}
								title={`${previewDeviceSettings.label} landing page preview`}
								className={previewDeviceSettings.frameClassName}
								onHeightChange={handlePreviewFrameHeightChange}
							>
								<main
									className="landing-font-preview landing-page relative min-h-screen w-full"
									style={previewStyle}
								>
									{isLockedByAiProposal ? (
										<AiPreviewBlocks
											blocks={aiPreviewBlocks}
											removedBlocks={aiPreviewPage?.removedBlocks ?? []}
											navigationPreview={navigationPreview}
										/>
									) : (
										<>
											<LayoutGroup>
												{blocks.map((block, index) => {
													const blockId = blockIds[index]!
													return (
														<Fragment key={blockId}>
															{index === 0 && (
																<div className="relative h-0">
																	<AddBlockIndicator
																		position="top"
																		label="Add a block at the top of the page"
																		onClick={() =>
																			setAddBlockRequest({
																				mode: "choose-type",
																				insertionIndex: 0,
																			})
																		}
																	/>
																</div>
															)}
															<AnimatedBlockWrapper
																id={getBlockDomId(blockId)}
																isMoved={() => movedBlockId.current === blockId}
																onMovedAnimationComplete={() => {
																	if (movedBlockId.current === blockId)
																		movedBlockId.current = null
																}}
															>
																<BlockPreview
																	block={block}
																	index={index}
																	navigationPreview={navigationPreview}
																	onMoveUp={
																		index > 0
																			? () => handleReorderBlock(index, index - 1)
																			: undefined
																	}
																	onMoveDown={
																		index < blocks.length - 1
																			? () => handleReorderBlock(index, index + 1)
																			: undefined
																	}
																	onEdit={() => setEditingBlockIndex(index)}
																	onDelete={() => handleDeleteBlock(index)}
																	isEditing={editingBlockIndex === index}
																	onChange={handleBlockChange}
																	onCancelEdit={() => setEditingBlockIndex(null)}
																/>
															</AnimatedBlockWrapper>
															<div className="relative h-0">
																<AddBlockIndicator
																	position={
																		index === blocks.length - 1
																			? "bottom"
																			: "between"
																	}
																	label={
																		index === blocks.length - 1
																			? "Add a block at the bottom of the page"
																			: `Add a block between blocks ${index + 1} and ${index + 2}`
																	}
																	onClick={() =>
																		setAddBlockRequest({
																			mode: "choose-type",
																			insertionIndex: index + 1,
																		})
																	}
																/>
															</div>
														</Fragment>
													)
												})}
											</LayoutGroup>
											{blocks.length === 0 && (
												<div className="flex min-h-125 flex-col items-center justify-center px-6 py-32 text-center">
													<div
														className="text-landing-foreground-subtle mb-6 opacity-60"
														style={{ animation: "float 3s ease-in-out infinite" }}
													>
														<IconStack2 className="size-20" />
													</div>
													<h2 className="text-landing-foreground mb-3 text-2xl font-bold">
														Start Building Your Landing Page
													</h2>
													<p className="text-landing-foreground-muted max-w-sm text-base leading-relaxed">
														Add your first block here or choose one from the sidebar. You
														can drag and drop to reorder blocks anytime.
													</p>
													<button
														type="button"
														onClick={() =>
															setAddBlockRequest({
																mode: "choose-type",
																insertionIndex: 0,
															})
														}
														className="mt-6 flex cursor-pointer items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-2.5 text-sm font-bold text-indigo-700 transition-colors hover:border-indigo-300 hover:bg-indigo-100"
													>
														<IconPlus className="size-4" />
														Add first block
													</button>
												</div>
											)}
										</>
									)}
								</main>
							</LandingPagePreviewFrame>
						</div>
					</div>
				</div>
				{canUseAi && (
					<AiWebsiteEditChat
						ai={ai}
						isExpanded={isAiPanelExpanded}
						onExpandedChange={handleAiPanelExpandedChange}
					/>
				)}
			</div>

			<CreatePageModal
				isOpen={isCreatePageOpen}
				pages={pages}
				onClose={() => setCreatePageOpen(false)}
				onCreated={async pageId => {
					await refetchPages()
					setSelectedPageId(pageId)
					setCreatePageOpen(false)
				}}
			/>
			<ManagePagesModal
				isOpen={isManagePagesOpen}
				pages={pages}
				externalLinks={externalNavigationLinks}
				domain={platform.domain.landingPage ?? platform.domain.platform}
				onClose={async () => {
					setManagePagesOpen(false)
					await Promise.all([refetchPages(), refetchExternalNavigationLinks()])
				}}
				onRefresh={async () => {
					await Promise.all([refetchPages(), refetchExternalNavigationLinks()])
				}}
			/>
			<AiCreatePageModal
				isOpen={isAiCreatePageModalOpen && canUseAi && !isLockedByAiProposal}
				defaultLanguage={platform.language}
				onClose={() => setAiCreatePageModalOpen(false)}
				onGenerated={async (pageId, generationId) => {
					setPageGenerationIds(ids => ({ ...ids, [pageId]: generationId }))
					setEditingBlockIndex(null)
					await Promise.all([refetchPages(), user.reload()])
					setSelectedPageId(pageId)
					toast.showSuccess("Page generated with AI")
				}}
			/>
			<AddBlockModal
				request={addBlockRequest}
				blocks={blocks}
				pageLabel={selectedPageLabel}
				onAdd={handleAddBlock}
				onClose={() => setAddBlockRequest(null)}
			/>
		</>
	)
}

type CreatePageModalProps = {
	isOpen: boolean
	pages: LandingPageSitePage[]
	onClose(): void
	onCreated(pageId: number): void
}

const CreatePageModal: FC<CreatePageModalProps> = ({ isOpen, pages, onClose, onCreated }) => {
	const alert = useAlert()
	const createMutation = query.landing.pages.create.useMutation()
	const [title, setTitle] = useState("")

	const canCreate = title.trim().length > 0 && !createMutation.isPending

	const handleCreate = useCallback(async () => {
		if (!canCreate) return
		try {
			const page = await createMutation.mutateAsync({
				path: createPagePathFromTitle(title, pages),
				title: title.trim(),
			})
			setTitle("")
			onCreated(page.id)
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to create page. Try a different page name.")
		}
	}, [alert, canCreate, createMutation, onCreated, pages, title])

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} onSubmit={handleCreate}>
			<ModalTitle>Create page</ModalTitle>
			<ModalText>Name the page</ModalText>
			<ModalInputs>
				<InputField label="Title" placeholder="Pricing" value={title} onChange={setTitle} />
			</ModalInputs>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose} isDisabled={createMutation.isPending}>
					Cancel
				</Button>
				<Button
					variant="primary"
					onClick={handleCreate}
					isDisabled={!canCreate}
					isLoading={createMutation.isPending}
				>
					Create page
				</Button>
			</ModalButtons>
		</DefaultModal>
	)
}

type ManagePagesModalProps = {
	isOpen: boolean
	pages: LandingPageSitePage[]
	externalLinks: LandingPageExternalNavigationLink[]
	domain: string
	onClose(): void
	onRefresh(): Promise<unknown>
}

const ManagePagesModal: FC<ManagePagesModalProps> = ({ isOpen, pages, externalLinks, domain, onClose, onRefresh }) => {
	const alert = useAlert()
	const platform = usePlatform()!

	const updateMetaMutation = query.landing.pages.updateMeta.useMutation()
	const updateNavMutation = query.landing.pages.updateNav.useMutation()
	const updateVisibilityMutation = query.landing.pages.updateNav.useMutation({
		onSuccess: async () => {
			await onRefresh()
		},
		onError: async error => {
			console.error(error)
			await alert.show("Error", "Failed to update navigation visibility.")
		},
	})
	const reorderNavigationMutation = query.landing.navigation.reorder.useMutation()
	const deleteMutation = query.landing.pages.delete.useMutation()
	const createExternalLinkMutation = query.landing.externalNavigationLinks.create.useMutation()
	const updateExternalLinkMutation = query.landing.externalNavigationLinks.update.useMutation()
	const deleteExternalLinkMutation = query.landing.externalNavigationLinks.delete.useMutation()

	const [search, setSearch] = useState("")
	const [editingPageId, setEditingPageId] = useState<number | null>(null)
	const [editingTitle, setEditingTitle] = useState("")
	const [editingPath, setEditingPath] = useState("")
	const [editingNavigationGroup, setEditingNavigationGroup] = useState("")
	const [isEditingDescriptionCustom, setEditingDescriptionCustom] = useState(false)
	const [editingDescription, setEditingDescription] = useState("")
	const [isExternalLinkFormOpen, setExternalLinkFormOpen] = useState(false)
	const [editingExternalLinkId, setEditingExternalLinkId] = useState<number | null>(null)
	const [externalLinkTitle, setExternalLinkTitle] = useState("")
	const [externalLinkUrl, setExternalLinkUrl] = useState("")
	const [externalLinkNavigationGroup, setExternalLinkNavigationGroup] = useState("")

	const navigationEntries = [
		...pages.map(page => ({ type: "page" as const, id: page.id, sortOrder: page.sortOrder, page })),
		...externalLinks.map(link => ({
			type: "externalLink" as const,
			id: link.id,
			sortOrder: link.sortOrder,
			link,
		})),
	].sort((a, b) => a.sortOrder - b.sortOrder)
	const isSearchable = navigationEntries.length > searchablePageCount
	const trimmedSearch = search.trim().toLowerCase()
	const isSearching = isSearchable && trimmedSearch.length > 0
	const visibleNavigationEntries = navigationEntries
		.map((entry, index) => ({ entry, index }))
		.filter(({ entry }) => {
			if (!isSearching) return true
			if (entry.type === "page") {
				return (
					(entry.page.title ?? "").toLowerCase().includes(trimmedSearch) ||
					entry.page.path.toLowerCase().includes(trimmedSearch)
				)
			}
			return (
				entry.link.title.toLowerCase().includes(trimmedSearch) ||
				entry.link.url.toLowerCase().includes(trimmedSearch)
			)
		})

	const editingPage = pages.find(page => page.id === editingPageId) ?? null
	const isEditingHomePage = editingPage?.path === "/"
	const canonicalEditingPath = normalizePagePathInput(editingPath)
	const canSaveEdit =
		editingPageId !== null &&
		editingTitle.trim().length > 0 &&
		editingPath.trim().length > 0 &&
		(!isEditingDescriptionCustom || editingDescription.trim().length > 0) &&
		(!isEditingHomePage || canonicalEditingPath === "/")
	const canDeletePages = pages.length > 1
	const normalizedExternalLinkUrl = normalizeExternalNavigationUrlInput(externalLinkUrl)
	const canSaveExternalLink =
		externalLinkTitle.trim().length > 0 &&
		isValidExternalNavigationUrl(normalizedExternalLinkUrl) &&
		!createExternalLinkMutation.isPending &&
		!updateExternalLinkMutation.isPending

	const handleEdit = useCallback((page: LandingPageSitePage) => {
		setEditingPageId(page.id)
		setEditingTitle(page.title ?? "")
		setEditingPath(page.path)
		setEditingNavigationGroup(page.navigationGroup ?? "")
		setEditingDescriptionCustom(page.description !== null)
		setEditingDescription(page.description ?? "")
	}, [])

	const resetPageEditForm = useCallback(() => {
		setEditingPageId(null)
		setEditingTitle("")
		setEditingPath("")
		setEditingNavigationGroup("")
		setEditingDescriptionCustom(false)
		setEditingDescription("")
	}, [])

	const handleUpdateMeta = useCallback(async () => {
		if (!canSaveEdit || editingPageId === null) return
		try {
			await updateMetaMutation.mutateAsync({
				pageId: editingPageId,
				title: editingTitle.trim(),
				path: canonicalEditingPath,
				// Null makes the page fall back to the academy description.
				description: isEditingDescriptionCustom ? editingDescription.trim() : null,
			})
			await updateNavMutation.mutateAsync({
				pageId: editingPageId,
				navigationGroup: normalizeNavigationGroupInput(editingNavigationGroup),
			})
			resetPageEditForm()
			await onRefresh()
		} catch (error) {
			console.error(error)
			await alert.show("Error", error instanceof Error ? error.message : "Failed to update page.")
		}
	}, [
		alert,
		canSaveEdit,
		editingDescription,
		editingNavigationGroup,
		editingPageId,
		editingTitle,
		canonicalEditingPath,
		isEditingDescriptionCustom,
		onRefresh,
		resetPageEditForm,
		updateMetaMutation,
		updateNavMutation,
	])

	const handleMoveNavigationEntry = async (type: "page" | "externalLink", id: number, direction: -1 | 1) => {
		const index = navigationEntries.findIndex(entry => entry.type === type && entry.id === id)
		const targetIndex = index + direction
		if (index === -1 || targetIndex < 0 || targetIndex >= navigationEntries.length) return
		const next = arrayMove([...navigationEntries], index, targetIndex).map(entry => ({
			type: entry.type,
			id: entry.id,
		}))
		try {
			await reorderNavigationMutation.mutateAsync({ entriesInOrder: next })
		} catch (error) {
			console.error(error)
			await onRefresh()
			await alert.show("Error", "Failed to change the navigation order. Please try again.")
			return
		}
		await onRefresh()
	}

	const handleVisibilityChange = useCallback(
		(pageId: number, placement: "header" | "footer", isVisible: boolean) => {
			updateVisibilityMutation.mutate(
				placement === "header"
					? { pageId, isHiddenInHeaderNavigation: !isVisible }
					: { pageId, isHiddenInFooterNavigation: !isVisible },
			)
		},
		[updateVisibilityMutation],
	)

	const resetExternalLinkForm = useCallback(() => {
		setExternalLinkFormOpen(false)
		setEditingExternalLinkId(null)
		setExternalLinkTitle("")
		setExternalLinkUrl("")
		setExternalLinkNavigationGroup("")
	}, [])

	const handleEditExternalLink = useCallback((link: LandingPageExternalNavigationLink) => {
		setExternalLinkFormOpen(true)
		setEditingExternalLinkId(link.id)
		setExternalLinkTitle(link.title)
		setExternalLinkUrl(link.url)
		setExternalLinkNavigationGroup(link.navigationGroup ?? "")
	}, [])

	const handleSaveExternalLink = useCallback(async () => {
		if (!canSaveExternalLink) return
		const data = {
			title: externalLinkTitle.trim(),
			url: normalizedExternalLinkUrl,
			navigationGroup: normalizeNavigationGroupInput(externalLinkNavigationGroup),
		}
		try {
			if (editingExternalLinkId === null) await createExternalLinkMutation.mutateAsync(data)
			else await updateExternalLinkMutation.mutateAsync({ linkId: editingExternalLinkId, ...data })
			resetExternalLinkForm()
			await onRefresh()
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to save the external navigation link.")
		}
	}, [
		alert,
		canSaveExternalLink,
		createExternalLinkMutation,
		editingExternalLinkId,
		externalLinkNavigationGroup,
		externalLinkTitle,
		normalizedExternalLinkUrl,
		onRefresh,
		resetExternalLinkForm,
		updateExternalLinkMutation,
	])

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen}>
			<ModalTitle>Manage pages</ModalTitle>
			<ModalText>Manage page visibility and add external destinations to your header navigation.</ModalText>
			<ModalInputs>
				<div className="mt-2 flex flex-col gap-3 rounded-xl border border-sky-100 bg-sky-50/70 p-3">
					<div className="flex items-center justify-between gap-3">
						<div>
							<div className="flex items-center gap-1.5 text-xs font-bold tracking-widest text-gray-700 uppercase">
								<IconExternalLink className="size-4 text-sky-600" />
								External header links
							</div>
							<p className="mt-1 text-xs text-gray-500">
								Link to a blog, social profile, or partner site.
							</p>
						</div>
						{!isExternalLinkFormOpen && (
							<Button
								variant="secondary"
								onClick={() => setExternalLinkFormOpen(true)}
								className="h-8 shrink-0 rounded-lg px-2.5 py-1.5 text-xs"
							>
								<IconPlus className="size-3.5" />
								Add link
							</Button>
						)}
					</div>

					{isExternalLinkFormOpen && (
						<div className="grid gap-3 rounded-lg border border-sky-200 bg-white p-3 shadow-sm">
							<div className="text-xs font-bold tracking-widest text-sky-700 uppercase">
								{editingExternalLinkId === null ? "Add external link" : "Edit external link"}
							</div>
							<InputField
								label="Link label"
								placeholder="My blog"
								value={externalLinkTitle}
								onChange={setExternalLinkTitle}
								maxCharacters={120}
							/>
							<InputField
								label="External URL"
								placeholder="https://example.com"
								inputType="url"
								value={externalLinkUrl}
								onChange={setExternalLinkUrl}
							/>
							<InputField
								label="Header dropdown label (optional)"
								placeholder="Resources"
								value={externalLinkNavigationGroup}
								onChange={setExternalLinkNavigationGroup}
								maxCharacters={120}
							/>
							<div className="flex items-center gap-2">
								<Button
									variant="primary"
									onClick={handleSaveExternalLink}
									isDisabled={!canSaveExternalLink}
									isLoading={
										createExternalLinkMutation.isPending || updateExternalLinkMutation.isPending
									}
								>
									{editingExternalLinkId === null ? "Add to header" : "Save link"}
								</Button>
								<Button
									variant="secondary"
									onClick={resetExternalLinkForm}
									isDisabled={
										createExternalLinkMutation.isPending || updateExternalLinkMutation.isPending
									}
								>
									Cancel
								</Button>
							</div>
						</div>
					)}
				</div>

				{editingPageId !== null && (
					<div className="mt-2 grid gap-3 rounded-lg border border-indigo-100 bg-indigo-50 p-3">
						<div className="text-xs font-bold tracking-widest text-gray-700 uppercase">Edit page</div>
						<InputField
							label="Title"
							placeholder="Pricing"
							value={editingTitle}
							onChange={setEditingTitle}
						/>
						<EditableLinkField
							label="Page link"
							prefix={`https://${domain}`}
							value={editingPath}
							onChange={setEditingPath}
							placeholder="/pricing"
							maxLength={landingPagePathMaxLength}
							warningText="Changing the slug will change this page link. The previous link will not lead to the page any more."
							displayValue={getDisplayedPagePath(editingPath)}
							isReadOnly={isEditingHomePage}
							readOnlyMessage="The home page always uses /."
							normalizeChange={normalizePagePathInputValue}
						/>
						<InputField
							label="Header dropdown label"
							placeholder="Resources"
							value={editingNavigationGroup}
							onChange={setEditingNavigationGroup}
						/>
						<div className="grid gap-2">
							<Checkbox
								label="Use a custom SEO description for this page"
								isChecked={isEditingDescriptionCustom}
								onChange={setEditingDescriptionCustom}
							/>
							{isEditingDescriptionCustom ? (
								<InputTextArea
									ariaLabel="SEO description"
									placeholder={platform.description ?? "What visitors find on this page"}
									value={editingDescription}
									onChange={setEditingDescription}
									maxCharacters={landingPagePageDescriptionMaxLength}
								/>
							) : (
								<p className="text-xs leading-relaxed text-gray-500">
									{platform.description
										? `Search engines show your academy description: "${platform.description}"`
										: "Search engines show your academy description, which you can set in the academy settings."}
								</p>
							)}
						</div>
						<div className="flex items-center gap-2">
							<Button
								variant="primary"
								onClick={handleUpdateMeta}
								isDisabled={!canSaveEdit}
								isLoading={updateMetaMutation.isPending || updateNavMutation.isPending}
							>
								Save page
							</Button>
							<Button
								variant="secondary"
								onClick={resetPageEditForm}
								isDisabled={updateMetaMutation.isPending || updateNavMutation.isPending}
							>
								Cancel
							</Button>
						</div>
					</div>
				)}

				<div className="mt-2 flex flex-col gap-2">
					<div className="flex items-center justify-between gap-3">
						<div className="text-xs font-bold tracking-widest text-gray-500 uppercase">
							Navigation order
						</div>
						{isSearchable && (
							<div className="text-xs text-gray-500">
								{navigationEntries.length} items
								{isSearching && ` · ${visibleNavigationEntries.length} matching`}
							</div>
						)}
					</div>
					{isSearchable && (
						<label className="block">
							<span className="sr-only">Search navigation</span>
							<InputField value={search} onChange={setSearch} placeholder="Search navigation" compact />
						</label>
					)}
					{visibleNavigationEntries.length === 0 ? (
						<div className="text-sm text-gray-500">No navigation items match your search.</div>
					) : (
						visibleNavigationEntries.map(({ entry, index }) => {
							if (entry.type === "externalLink") {
								const link = entry.link
								return (
									<div
										key={`external-${link.id}`}
										className="flex items-center justify-between gap-3 rounded-lg border border-sky-100 bg-sky-50/40 px-3 py-2"
									>
										<div className="min-w-0">
											<div className="flex items-center gap-1.5">
												<IconExternalLink className="size-3.5 shrink-0 text-sky-600" />
												<div className="truncate text-sm font-semibold text-gray-900">
													{link.title}
												</div>
											</div>
											<a
												href={link.url}
												target="_blank"
												rel="noopener noreferrer"
												className="block truncate text-xs text-sky-700 hover:underline"
											>
												{link.url}
											</a>
											{link.navigationGroup && (
												<div className="truncate text-xs font-semibold text-indigo-600">
													Header dropdown: {link.navigationGroup}
												</div>
											)}
										</div>
										<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
											{!isSearching && (
												<>
													<button
														type="button"
														className="cursor-pointer rounded-md border border-black/10 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
														disabled={index === 0 || reorderNavigationMutation.isPending}
														onClick={() =>
															handleMoveNavigationEntry("externalLink", link.id, -1)
														}
													>
														Up
													</button>
													<button
														type="button"
														className="cursor-pointer rounded-md border border-black/10 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
														disabled={
															index === navigationEntries.length - 1 ||
															reorderNavigationMutation.isPending
														}
														onClick={() =>
															handleMoveNavigationEntry("externalLink", link.id, 1)
														}
													>
														Down
													</button>
												</>
											)}
											<button
												type="button"
												className="cursor-pointer rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
												disabled={deleteExternalLinkMutation.isPending}
												onClick={async () => {
													const dialog = await alert.confirm(
														"Delete navigation link",
														`Remove “${link.title}” from your header?`,
													)
													if (!dialog.result) return
													await deleteExternalLinkMutation.mutateAsync({ linkId: link.id })
													if (editingExternalLinkId === link.id) resetExternalLinkForm()
													await onRefresh()
													dialog.close()
												}}
											>
												Delete
											</button>
											<button
												type="button"
												className="cursor-pointer rounded-md border border-black/10 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
												onClick={() => handleEditExternalLink(link)}
											>
												Edit
											</button>
										</div>
									</div>
								)
							}

							const p = entry.page
							const visibilityVariables = updateVisibilityMutation.variables
							const isHeaderVisibilityPending =
								updateVisibilityMutation.isPending &&
								visibilityVariables?.pageId === p.id &&
								visibilityVariables.isHiddenInHeaderNavigation !== undefined
							const isFooterVisibilityPending =
								updateVisibilityMutation.isPending &&
								visibilityVariables?.pageId === p.id &&
								visibilityVariables.isHiddenInFooterNavigation !== undefined

							return (
								<div
									key={`page-${p.id}`}
									className="flex items-center justify-between gap-3 rounded-lg border border-black/10 bg-white px-3 py-2"
								>
									<div className="min-w-0">
										<div className="truncate text-sm font-semibold text-gray-900">
											{p.title ?? p.path}
										</div>
										<div className="truncate text-xs text-gray-500">{p.path}</div>
										{p.navigationGroup && !p.isHiddenInHeaderNavigation && (
											<div className="truncate text-xs font-semibold text-indigo-600">
												Header dropdown: {p.navigationGroup}
											</div>
										)}
									</div>
									<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
										{!isSearching && (
											<>
												<button
													type="button"
													className="cursor-pointer rounded-md border border-black/10 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
													disabled={index === 0 || reorderNavigationMutation.isPending}
													onClick={() => handleMoveNavigationEntry("page", p.id, -1)}
												>
													Up
												</button>
												<button
													type="button"
													className="cursor-pointer rounded-md border border-black/10 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
													disabled={
														index === navigationEntries.length - 1 ||
														reorderNavigationMutation.isPending
													}
													onClick={() => handleMoveNavigationEntry("page", p.id, 1)}
												>
													Down
												</button>
											</>
										)}
										<label
											className={cn(
												"flex cursor-pointer items-center gap-1.5 rounded-md border border-black/10 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50",
												updateVisibilityMutation.isPending &&
													"cursor-auto opacity-60 hover:bg-white",
											)}
										>
											{isHeaderVisibilityPending ? (
												<IconLoader2 className="size-4 animate-spin text-indigo-600" />
											) : (
												<input
													type="checkbox"
													className="size-4 cursor-pointer accent-indigo-600 disabled:cursor-auto"
													checked={!p.isHiddenInHeaderNavigation}
													disabled={updateVisibilityMutation.isPending}
													onChange={event =>
														handleVisibilityChange(
															p.id,
															"header",
															event.currentTarget.checked,
														)
													}
												/>
											)}
											Header
										</label>
										<label
											className={cn(
												"flex cursor-pointer items-center gap-1.5 rounded-md border border-black/10 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50",
												updateVisibilityMutation.isPending &&
													"cursor-auto opacity-60 hover:bg-white",
											)}
										>
											{isFooterVisibilityPending ? (
												<IconLoader2 className="size-4 animate-spin text-indigo-600" />
											) : (
												<input
													type="checkbox"
													className="size-4 cursor-pointer accent-indigo-600 disabled:cursor-auto"
													checked={!p.isHiddenInFooterNavigation}
													disabled={updateVisibilityMutation.isPending}
													onChange={event =>
														handleVisibilityChange(
															p.id,
															"footer",
															event.currentTarget.checked,
														)
													}
												/>
											)}
											Footer
										</label>
										{p.path !== "/" && pages.length > 1 ? (
											<button
												type="button"
												className="cursor-pointer rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
												disabled={!canDeletePages || deleteMutation.isPending}
												onClick={async () => {
													const dialog = await alert.confirm(
														"Delete page",
														`Delete “${p.title ?? p.path}”?`,
													)
													if (!dialog.result) return
													await deleteMutation.mutateAsync({ pageId: p.id })
													await onRefresh()
													dialog.close()
												}}
											>
												Delete
											</button>
										) : (
											<Tooltip
												tooltip={
													p.path === "/"
														? "You cannot delete the home page"
														: "You cannot delete the last page"
												}
											>
												<button
													type="button"
													className="cursor-pointer rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
													disabled
												>
													Delete
												</button>
											</Tooltip>
										)}
										<button
											type="button"
											className="cursor-pointer rounded-md border border-black/10 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
											onClick={() => handleEdit(p)}
										>
											Edit
										</button>
									</div>
								</div>
							)
						})
					)}
				</div>
			</ModalInputs>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose}>
					Done
				</Button>
			</ModalButtons>
		</DefaultModal>
	)
}

type AnimatedBlockWrapperProps = {
	id: string
	isMoved(): boolean
	onMovedAnimationComplete(): void
	children: ReactNode
}

const AnimatedBlockWrapper: FC<AnimatedBlockWrapperProps> = ({ isMoved, onMovedAnimationComplete, id, children }) => {
	const ref = useRef<HTMLDivElement>(null)

	return (
		<motion.div
			ref={ref}
			id={id}
			layout
			style={{ position: "relative", zIndex: 0 }}
			transition={{ type: "spring", stiffness: 300, damping: 30 }}
			onLayoutAnimationStart={() => {
				if (ref.current && isMoved()) {
					ref.current.style.zIndex = "10"
				}
			}}
			onLayoutAnimationComplete={() => {
				if (ref.current) ref.current.style.zIndex = "0"
				onMovedAnimationComplete()
			}}
		>
			{children}
		</motion.div>
	)
}

function getFirstAiChangeDomId(page: WebsiteEditPreview["pages"][number] | null): string | null {
	if (page === null) return null

	const blockIndex = page.blocks?.findIndex(entry => entry.change !== null) ?? -1
	const removedIndex = page.removedBlocks[0]?.index ?? null
	if (blockIndex === -1) return removedIndex !== null ? getAiPreviewRemovedBlockDomId(0) : null
	return removedIndex !== null && removedIndex <= blockIndex
		? getAiPreviewRemovedBlockDomId(0)
		: getAiPreviewBlockDomId(blockIndex)
}

function getBlockDomId(blockId: number): string {
	return `landing-editor-block-${blockId}`
}

function scrollElementIntoPreview(domId: string): void {
	const previewFrame = document.getElementById(previewFrameId) as HTMLIFrameElement | null
	const frameElement = previewFrame?.contentDocument?.getElementById(domId)
	// The preview column is what scrolls, since the editor row itself never overflows.
	const scroller = document.getElementById(previewScrollContainerId)
	if (!previewFrame || !frameElement || !scroller) return

	const frameRect = previewFrame.getBoundingClientRect()
	const scrollerRect = scroller.getBoundingClientRect()
	const targetRect = frameElement.getBoundingClientRect()
	const targetTop = scroller.scrollTop + (frameRect.top - scrollerRect.top) + targetRect.top
	scroller.scrollTo({
		top: Math.max(targetTop - scroller.clientHeight / 2 + targetRect.height / 2, 0),
		behavior: "auto",
	})
}

function createPagePathFromTitle(title: string, pages: LandingPageSitePage[]): string {
	const basePath = titleToPath(title)
	const existingPaths = new Set(pages.map(page => page.path))
	if (!existingPaths.has(basePath)) return basePath

	for (let index = 2; ; index++) {
		const path = buildPagePathCandidate(basePath, index)
		if (!existingPaths.has(path)) return path
	}
}

function titleToPath(title: string): string {
	const path = normalizePagePathInput(title)
	return path === "/" ? "/untitled-page" : path
}

function getDisplayedPagePath(path: string): string {
	return normalizePagePathInput(path)
}

function normalizePagePathInputValue(value: string): string {
	const trimmed = value.trim()
	if (trimmed.length === 0) return ""

	if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
		try {
			return new URL(trimmed).pathname || "/"
		} catch {
			return trimmed
		}
	}

	return trimmed.startsWith("/") ? trimmed : `/${trimmed}`
}

function normalizePagePathInput(value: string): string {
	const rawPath = normalizePagePathInputValue(value)
	const pathWithoutSlashes = rawPath.replace(/^\/+/, "").replace(/\/+$/, "")
	if (pathWithoutSlashes.length === 0) return "/"

	const slug = pathWithoutSlashes
		.split("/")
		.map(segment => toSlug(segment))
		.filter(segment => segment.length > 0)
		.join("/")

	const path = `/${slug}`.slice(0, landingPagePathMaxLength).replace(/[/-]+$/g, "")
	return path.length > 0 ? path : "/untitled-page"
}

function getAiPreviewThemeSettings(
	settings: LandingPageSettings,
	style: NonNullable<WebsiteEditPreview["style"]>,
): LandingThemeSettings {
	const preset = applyLandingPageStylePreset({
		styleId: style.styleId ?? undefined,
		fontId: style.fontId ?? undefined,
		buttonStyleId: style.buttonStyleId ?? undefined,
	})
	return {
		styleId: preset.styleId ?? settings.styleId,
		usesBrandColor: style.usesBrandColor ?? settings.usesBrandColor,
		fontId: preset.fontId ?? settings.fontId,
		buttonStyleId: preset.buttonStyleId ?? settings.buttonStyleId,
	}
}

function buildPagePathCandidate(basePath: string, index: number): string {
	const suffix = `-${index}`
	const basePathWithoutLeadingSlash = basePath === "/" ? "page" : basePath.replace(/^\/+/, "")
	const baseMaxLength = Math.max(landingPagePathMaxLength - 1 - suffix.length, 0)
	const truncatedBasePath = basePathWithoutLeadingSlash.slice(0, baseMaxLength).replace(/[/-]+$/g, "")
	return `/${truncatedBasePath.length > 0 ? truncatedBasePath : "page"}${suffix}`
}

function normalizeNavigationGroupInput(value: string): string | null {
	const trimmed = value.trim()
	return trimmed.length > 0 ? trimmed : null
}

function normalizeExternalNavigationUrlInput(value: string): string {
	const trimmed = value.trim()
	if (trimmed.length === 0 || /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed)) return trimmed
	return `https://${trimmed}`
}

function isValidExternalNavigationUrl(value: string): boolean {
	try {
		const url = new URL(value)
		return (url.protocol === "https:" || url.protocol === "http:") && url.hostname.length > 0
	} catch {
		return false
	}
}

function getNavigationVisibilityLabel(page: LandingPageSitePage): string {
	const isVisibleInHeader = !page.isHiddenInHeaderNavigation
	const isVisibleInFooter = !page.isHiddenInFooterNavigation
	if (isVisibleInHeader && isVisibleInFooter) return "Header + footer"
	if (isVisibleInHeader) return "Header"
	if (isVisibleInFooter) return "Footer"
	return "Hidden"
}

function getNavigationVisibilityBadgeClassName(page: LandingPageSitePage): string {
	const isVisibleInHeader = !page.isHiddenInHeaderNavigation
	const isVisibleInFooter = !page.isHiddenInFooterNavigation
	if (isVisibleInHeader && isVisibleInFooter) return "bg-emerald-100 text-emerald-700"
	if (isVisibleInHeader || isVisibleInFooter) return "bg-blue-100 text-blue-700"
	return "bg-gray-100 text-gray-500"
}

function getInsertionToastSuffix(blocks: LandingPageBlock[], insertionIndex: number): string {
	if (blocks.length === 0) return ""
	if (insertionIndex <= 0) return " to the top"
	if (insertionIndex >= blocks.length) return " to the bottom"
	return ` between ${blockDefinition(blocks[insertionIndex - 1]!.type).label} and ${blockDefinition(blocks[insertionIndex]!.type).label}`
}

function collectInputErrorMessages(value: unknown): string[] {
	if (typeof value !== "object" || value === null) return []
	const node = value as { errors?: unknown; properties?: unknown; items?: unknown }
	const messages = Array.isArray(node.errors) ? node.errors.filter(error => typeof error === "string") : []
	const flatMessages = Object.values(node).flatMap(value =>
		Array.isArray(value) ? value.filter(error => typeof error === "string") : [],
	)
	const propertyMessages =
		typeof node.properties === "object" && node.properties !== null
			? Object.values(node.properties).flatMap(collectInputErrorMessages)
			: []
	const itemMessages = Array.isArray(node.items) ? node.items.flatMap(collectInputErrorMessages) : []
	return [...messages, ...flatMessages, ...propertyMessages, ...itemMessages]
}

function getLandingPageSaveErrorMessage(error: unknown): string {
	const inputErrorMessages = collectInputErrorMessages(getInputErrors(error))
	const errorMessage = error instanceof Error ? error.message : ""
	const hasCharacterLimitError =
		inputErrorMessages.some(message => /^Must be \d+ characters or fewer\.$/.test(message)) ||
		/Must be \d+ characters or fewer\./.test(errorMessage)

	if (hasCharacterLimitError) {
		return "Some website copy is over its character limit. Please shorten fields marked in red and try again."
	}

	return "Failed to save landing page. Please try again."
}

export default LandingPageEditor
